import { useEffect, useRef } from "react";

/**
 * Scroll reveal for the marketing pages — the same behaviour the landing page
 * uses, lifted into a hook so every page animates identically.
 *
 * Mark any element with `data-reveal` (and optionally `data-reveal-delay` in
 * milliseconds). Falls back to simply showing everything when
 * IntersectionObserver is unavailable or the viewer asked for reduced motion.
 *
 * Also returns the page to the top on mount, since these pages are reached
 * from footer links part-way down another page.
 */
const useReveal = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll("[data-reveal]");
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      targets.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = Number(entry.target.dataset.revealDelay || 0);
          setTimeout(() => entry.target.classList.add("is-visible"), delay);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );

    targets.forEach((el) => observer.observe(el));

    /* Safety net. An element flicked past between two frames — the End key, a
       hard trackpad throw — can miss its intersection callback entirely and
       stay invisible. On a marketing page that would leave a blank section, so
       anything already scrolled past is revealed outright. */
    let queued = false;
    const sweep = () => {
      queued = false;
      const limit = window.innerHeight;
      root.querySelectorAll("[data-reveal]:not(.is-visible)").forEach((el) => {
        if (el.getBoundingClientRect().top < limit) {
          el.classList.add("is-visible");
          observer.unobserve(el);
        }
      });
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(sweep);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  return rootRef;
};

export default useReveal;
